// Data loader for local JSON files
// Supports compendium store (SRD cache + optional data pack) when in browser
function extractSavingThrows(rawMarkdown) {
  if (!rawMarkdown) return [];
  const savingThrowMatch = rawMarkdown.match(/Saving Throws[:\s]+([A-Z][a-z]+(?:\s+and\s+[A-Z][a-z]+)?)/i);
  if (savingThrowMatch) {
    const throws = savingThrowMatch[1].split(/\s+and\s+|\s+/);
    return throws.map(t => t.trim().toUpperCase().substring(0, 3));
  }
  return [];
}

function extractFlavorInfo(rawMarkdown) {
  if (!rawMarkdown) return {};
  const ageMatch = rawMarkdown.match(/## Age\s+([^#]+)/i);
  const heightMatch = rawMarkdown.match(/## Size\s+([^#]+)/i);
  return {
    age: ageMatch ? ageMatch[1].trim() : null,
    height_weight: heightMatch ? heightMatch[1].trim() : null,
  };
}

function normalizeProficiencies(profArray) {
  if (!Array.isArray(profArray)) return [];
  return profArray.flatMap(item => {
    if (typeof item === 'string') return [item];
    if (typeof item === 'object' && item !== null) {
      if (item.choose) {
        return [];
      }
      return Object.keys(item).filter(k => item[k] === true);
    }
    return [];
  });
}

function normalizeLanguages(langData) {
  if (typeof langData === 'number') return { count: langData, type: 'any' };
  if (Array.isArray(langData)) {
    return langData.map(lang => {
      if (typeof lang === 'string') return lang;
      if (typeof lang === 'object' && lang.anyStandard) {
        return { count: lang.anyStandard, type: 'anyStandard' };
      }
      return lang;
    });
  }
  return [];
}

function transformEntity(jsonData, entityType) {
  if (!jsonData || !jsonData.data) return null;
  
  const meta = jsonData.meta || {};
  const data = jsonData.data;
  const raw = jsonData.raw || {};
  
  const baseId = meta.slug || meta.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown';
  const entity = {
    id: baseId,
    name: meta.name || data.name,
    source: meta.source,
  };

  switch (entityType) {
    case 'Race':
      const speeds = data.speeds || {};
      const walkSpeed = typeof speeds === 'number' ? speeds : (speeds.walk || 30);
      // Climb/swim default to half walking speed rounded to nearest 5 if not listed
      const halfWalkRounded5 = Math.round(walkSpeed / 10) * 5;
      const senses = data.senses || {};
      const asi = data.ability_score_increase || {};
      const asiArray = Object.keys(asi).map(ability => ({
        ability: ability.toUpperCase(),
        bonus: asi[ability]
      }));
      const raceProfs = data.proficiencies || {};
      const flavorInfo = extractFlavorInfo(raw.markdown || '');
      const abilityBonuses = {};
      Object.keys(asi).forEach(key => {
        const value = asi[key];
        abilityBonuses[key] = value;
        abilityBonuses[key.toLowerCase()] = value;
        const abilityMap = {
          'STR': 'strength', 'DEX': 'dexterity', 'CON': 'constitution',
          'INT': 'intelligence', 'WIS': 'wisdom', 'CHA': 'charisma'
        };
        if (abilityMap[key]) {
          abilityBonuses[abilityMap[key]] = value;
        }
      });
      
      return {
        ...entity,
        description: data.description || data.description_md || raw.markdown || '',
        size: data.size || 'Medium',
        speed: walkSpeed,
        speeds: {
          walk: walkSpeed,
          climb: speeds.climb ?? halfWalkRounded5,
          swim: speeds.swim ?? halfWalkRounded5,
          fly: speeds.fly || null,
          burrow: speeds.burrow || null,
        },
        senses: {
          darkvision: senses.darkvision || null,
          blindsight: senses.blindsight || null,
          tremorsense: senses.tremorsense || null,
          truesight: senses.truesight || null,
        },
        ability_bonuses: abilityBonuses,
        ability_score_increases: asiArray,
        languages: Array.isArray(data.languages) ? data.languages : [],
        proficiencies: {
          weapons: raceProfs.weapons || [],
          armor: raceProfs.armor || [],
          tools: raceProfs.tools || [],
          skills: raceProfs.skills || data.skill_proficiencies || [],
        },
        traits: Array.isArray(data.traits) ? data.traits.map(t => ({
          name: t.name || '',
          description: t.description_md || t.description || '',
          action_type: t.action_type || 'passive',
          uses: t.uses || null,
        })) : [],
        subraces: data.subraces || [],
        age_range: flavorInfo.age || null,
        height_weight_range: flavorInfo.height_weight || null,
      };
    
    case 'Class':
      let savingThrows = data.saving_throws;
      if (!savingThrows || (Array.isArray(savingThrows) && savingThrows.length === 0)) {
        savingThrows = extractSavingThrows(raw.markdown || '');
      }
      if (Array.isArray(savingThrows)) {
        savingThrows = savingThrows.map(st => {
          if (typeof st === 'string') {
            const mapping = {
              'strength': 'STR', 'str': 'STR',
              'dexterity': 'DEX', 'dex': 'DEX',
              'constitution': 'CON', 'con': 'CON',
              'intelligence': 'INT', 'int': 'INT',
              'wisdom': 'WIS', 'wis': 'WIS',
              'charisma': 'CHA', 'cha': 'CHA'
            };
            const upper = st.toUpperCase();
            return mapping[st.toLowerCase()] || (upper.length <= 3 ? upper : st);
          }
          return st;
        });
      }
      
      const spellcasting = data.spellcasting || {};
      const spellcastingAbility = spellcasting.casting_ability || null;
      const featuresByLevel = data.features_by_level || [];
      const equipmentChoices = data.equipment_choices || data.starting_equipment || [];
      const multiclass = data.multiclass || {};
      let primaryAbility = data.primary_ability || [];
      if (!primaryAbility.length && spellcastingAbility) {
        primaryAbility = [spellcastingAbility];
      }
      const skillChoices = data.skill_choices || null;
      
      return {
        ...entity,
        description: raw.markdown || data.description_md || data.description || '',
        hit_die: data.hit_die || 8,
        hit_points_at_1st: (data.hit_die || 8) + ' + CON modifier',
        hit_points_at_higher: '1d' + (data.hit_die || 8) + ' (or ' + Math.floor((data.hit_die || 8) / 2 + 1) + ') + CON modifier',
        saving_throw_proficiencies: Array.isArray(savingThrows) ? savingThrows : [],
        primary_ability: primaryAbility,
        proficiencies: {
          armor: data.proficiencies?.armor || [],
          weapons: data.proficiencies?.weapons || [],
          tools: data.proficiencies?.tools || [],
        },
        armor_proficiencies: data.proficiencies?.armor || [],
        weapon_proficiencies: data.proficiencies?.weapons || [],
        tool_proficiencies: data.proficiencies?.tools || [],
        skill_choices: skillChoices,
        features_by_level: featuresByLevel.map(levelData => ({
          level: levelData.level,
          features: Array.isArray(levelData.features) ? levelData.features.map(f => ({
            name: f.name || '',
            description: f.description_md || f.description || '',
            action_type: f.action_type || null,
            uses: f.uses || null,
          })) : [],
        })),
        spellcasting: {
          type: spellcasting.type || (spellcasting.prepared ? 'prepared' : (spellcasting.known ? 'known' : (spellcasting.pact ? 'pact' : 'none'))),
          ability: spellcastingAbility,
          casting_ability: spellcastingAbility,
          cantrips: spellcasting.cantrips || null,
          spells_known: spellcasting.spells_known || null,
          spells_prepared: spellcasting.spells_prepared || null,
          slots_by_level: spellcasting.slots_by_level || null,
          spell_list: spellcasting.spell_list != null ? spellcasting.spell_list : (spellcasting.spell_list === undefined ? null : []),
          progression: spellcasting.progression || null,
        },
        spellcasting_ability: spellcastingAbility,
        starting_equipment: equipmentChoices,
        multiclassing: {
          prerequisites: multiclass.prereqs || [],
          proficiencies_gained: {
            armor: multiclass.gains?.armor || [],
            weapons: multiclass.gains?.weapons || [],
            tools: multiclass.gains?.tools || [],
          },
        },
        subclasses: Array.isArray(data.subclasses) ? data.subclasses : [],
        class_variants: Array.isArray(data.class_variants) ? data.class_variants.map(v => ({
          slug: v.slug || v.name?.toLowerCase().replace(/\s+/g, '-'),
          name: v.name,
          parent_class: v.parent_class,
          description: v.description_md || v.description || '',
          hit_die: v.hit_die,
          saving_throws: v.saving_throws || [],
          proficiencies: v.proficiencies || {},
          skill_choices: v.skill_choices || null,
          spellcasting: v.spellcasting || {},
          features_by_level: v.features_by_level || [],
          subclasses: v.subclasses || [],
        })) : [],
      };
    
    case 'Subclass':
      let parentClass = data.parent_class;
      if (!parentClass && meta.path) {
        const pathMatch = meta.path.match(/Classes[\/\\]([^\/\\]+)[\/\\]/i);
        if (pathMatch) {
          parentClass = pathMatch[1];
        }
      }
      
      return {
        ...entity,
        description: raw.markdown || data.description_md || data.description || '',
        parent_class: parentClass || null,
        features_by_level: Array.isArray(data.features_by_level) ? data.features_by_level.map(levelData => ({
          level: levelData.level,
          features: Array.isArray(levelData.features) ? levelData.features.map(f => ({
            name: f.name || '',
            description: f.description_md || f.description || '',
            action_type: f.action_type || null,
            uses: f.uses || null,
          })) : [],
        })) : [],
        bonus_spells: data.bonus_spells || [],
        replacements: data.replacements || [],
      };
    
    case 'Spell':
      const description = (data.description_md || data.description || '').toLowerCase();
      const name = (meta.name || '').toLowerCase();
      
      // Derive mechanics flags from data
      const hasAttackRoll = !!(data.attack_type || 
        description.includes('make a ranged spell attack') ||
        description.includes('make a melee spell attack') ||
        description.includes('make a spell attack'));
      
      const hasDamage = !!(data.damage && 
        (Array.isArray(data.damage) ? data.damage.length > 0 : Object.keys(data.damage).length > 0));
      
      const hasHealing = !!(data.healing && 
        (Array.isArray(data.healing) ? data.healing.length > 0 : Object.keys(data.healing).length > 0));
      
      const hasSave = !!(data.save || 
        description.match(/(strength|dexterity|constitution|intelligence|wisdom|charisma)\s+save/i));
      
      const isAutoHit = description.includes('automatically hits') || 
                        description.includes('always hits') ||
                        name.includes('magic missile') ||
                        (description.includes('hits a creature') && 
                         !description.includes('make a') && 
                         !description.includes('spell attack'));
      
      return {
        ...entity,
        level: data.level || 0,
        school: data.school || 'Evocation',
        casting_time: data.casting_time || '1 Action',
        range: data.range || 'Self',
        components: {
          verbal: data.components?.v || data.components?.verbal || false,
          somatic: data.components?.s || data.components?.somatic || false,
          material: data.components?.m !== null && data.components?.m !== undefined,
          materials_needed: typeof data.components?.m === 'string' ? data.components.m : (data.materials_needed || null),
        },
        duration: data.duration || 'Instantaneous',
        description: data.description_md || data.description || '',
        higher_levels: data.higher_levels_md || data.higher_levels || null,
        ritual: data.ritual || false,
        concentration: data.concentration || false,
        classes: data.classes || [],
        tags: meta.tags || [],
        meta: {
          ...meta,
          tags: meta.tags || [],
        },
        // Mechanics flags
        has_attack_roll: hasAttackRoll,
        has_damage: hasDamage,
        has_healing: hasHealing,
        has_save: hasSave,
        is_auto_hit: isAutoHit,
      };
    
    case 'Background':
      return {
        ...entity,
        description: data.description_md || data.description || '',
        skill_proficiencies: normalizeProficiencies(data.skill_proficiencies || []),
        tool_proficiencies: normalizeProficiencies(data.tool_proficiencies || []),
        languages: normalizeLanguages(data.languages),
        equipment: data.equipment || '',
        feature: data.feature || null,
        personality_traits: data.personality_traits || [],
        ideals: data.ideals || [],
        bonds: data.bonds || [],
        flaws: data.flaws || [],
      };
    
    case 'Monster': {
      const cr = data.challenge?.cr || data.challenge_rating || 0;
      const ac = data.ac?.value || data.armor_class || 10;
      const acNotes = data.ac?.notes || data.armor_description || null;
      const hp = data.hp?.average || data.hit_points?.average || data.hit_points || null;
      const hpFormula = data.hp?.formula || data.hit_points?.dice || null;
      const speeds = data.speeds || data.speed || {};
      const abilityScores = data.ability_scores || {
        STR: 10,
        DEX: 10,
        CON: 10,
        INT: 10,
        WIS: 10,
        CHA: 10,
      };
      const profs = data.proficiencies || {};
      const savingThrows = profs.saving_throws || data.saving_throws || [];
      const skills = profs.skills || data.skills || [];
      const damageVulns = data.vulnerabilities || data.damage_vulnerabilities || [];
      const damageResists = data.resistances || data.damage_resistances || [];
      const damageImmunes = data.immunities || data.damage_immunities || [];
      const conditionImmunes = data.condition_immunities || [];
      const senses = data.senses || {};
      const traits = Array.isArray(data.traits) ? data.traits : [];
      const actions = Array.isArray(data.actions) ? data.actions : [];
      const reactions = Array.isArray(data.reactions) ? data.reactions : [];
      const legendaryActions = Array.isArray(data.legendary_actions) ? data.legendary_actions : [];
      
      return {
        ...entity,
        type: data.type || 'Unknown',
        size: data.size || 'Medium',
        alignment: data.alignment || null,
        challenge_rating: cr,
        armor_class: ac,
        armor_description: acNotes,
        hit_points: {
          average: hp,
          dice: hpFormula,
        },
        speed: speeds,
        ability_scores: abilityScores,
        saving_throws: savingThrows,
        skills: skills,
        damage_vulnerabilities: damageVulns,
        damage_resistances: damageResists,
        damage_immunities: damageImmunes,
        condition_immunities: conditionImmunes,
        senses: senses,
        languages: data.languages || null,
        traits: traits,
        actions: actions,
        reactions: reactions,
        legendary_actions: legendaryActions,
        description: raw.markdown || data.description_md || data.description || null,
      };
    }
    
    case 'MagicItem':
      return {
        ...entity,
        type: data.type || 'Wondrous Item',
        rarity: data.rarity || 'Common',
        requires_attunement: data.requires_attunement || false,
        attunement_requirements: data.attunement_requirements || null,
        description: data.description_md || data.description || '',
        effects: data.effects || [],
        weight: data.weight || null,
        value: data.value || null,
      };
    
    case 'Feat':
      return {
        ...entity,
        description: data.description_md || data.description || '',
        prerequisites: data.prerequisites || [],
        ability_score_increase: data.ability_score_increase || null,
        benefits: data.benefits || [],
        category: data.category || 'General',
      };
    
    default:
      return { ...entity, ...data };
  }
}

// Keys must match entityType.toLowerCase() (e.g. 'class', 'magicitem', 'subclass')
const dataCache = {
  race: null,
  class: null,
  subclass: null,
  spell: null,
  background: null,
  monster: null,
  magicitem: null,
  feat: null,
};

/** In-flight load promises by entity type to deduplicate concurrent loadEntityData(type) calls */
const inFlightLoads = new Map();

async function loadEntityData(entityType, directory) {
  const entityTypeLower = entityType.toLowerCase();

  if (dataCache[entityTypeLower]) {
    return dataCache[entityTypeLower];
  }

  if (inFlightLoads.has(entityTypeLower)) {
    return inFlightLoads.get(entityTypeLower);
  }

  const loadPromise = (async () => {
    try {
    // In browser: try compendium store first (SRD + optional data pack)
    if (typeof window !== 'undefined' && typeof indexedDB !== 'undefined') {
      try {
        const { getMergedRaw } = await import('@/utils/compendiumStore');
        const rawList = await getMergedRaw(entityType);
        if (rawList && rawList.length > 0) {
          const entities = [];
          let index = 0;
          for (const raw of rawList) {
            const jsonData = Array.isArray(raw) ? { meta: { name: raw.name, source: raw.source, slug: raw.name?.toLowerCase?.()?.replace(/\s+/g, '-') }, data: raw } : raw;
            const entity = transformEntity(jsonData, entityType);
            if (entity) {
              if (entities.find((e) => e.id === entity.id)) {
                entity.id = `${entity.id}-${index}`;
              }
              entities.push(entity);
              index++;
            }
          }
          dataCache[entityTypeLower] = entities;
          return entities;
        }
      } catch (err) {
        // No compendium data; do not cache empty so next load can retry (e.g. after SRD ready)
      }
    }
    // Do not cache empty: allows retry when compendium loads later (e.g. after server→local data change)
    return [];
  } catch (error) {
    return [];
  }
  })();

  inFlightLoads.set(entityTypeLower, loadPromise);
  loadPromise.finally(() => { inFlightLoads.delete(entityTypeLower); });
  return loadPromise;
}

// Generator/reference data (names, treasure, magic item tables) - from compendium bundle
export async function loadNames() {
  try {
    if (typeof window !== 'undefined') {
      const { getNames } = await import('@/utils/compendiumStore');
      return await getNames();
    }
    return null;
  } catch {
    return null;
  }
}

export async function loadTreasure() {
  try {
    if (typeof window !== 'undefined') {
      const { getTreasure } = await import('@/utils/compendiumStore');
      return await getTreasure();
    }
    return null;
  } catch {
    return null;
  }
}

export async function loadMagicItemTable() {
  try {
    if (typeof window !== 'undefined') {
      const { getMagicItemTables } = await import('@/utils/compendiumStore');
      return await getMagicItemTables();
    }
    return null;
  } catch {
    return null;
  }
}

export { loadEntityData, transformEntity };

/**
 * Clear in-memory entity cache so next loadEntityData() will refetch
 * (e.g. after user adds or removes a data pack).
 */
export function clearCompendiumCache() {
  dataCache.race = null;
  dataCache.class = null;
  dataCache.subclass = null;
  dataCache.spell = null;
  dataCache.background = null;
  dataCache.monster = null;
  dataCache.magicitem = null;
  dataCache.feat = null;
}

